import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiUtils';

/**
 * ConnectionActivityPanel
 *
 * Shows every automated job that has touched a connection over the last
 * 7 days, aggregated by job_type. Lets a user confirm whether monitoring
 * is actually running (or actually paused) without having to dig through
 * logs or the warehouse query history.
 *
 * Wired to: GET /api/connections/:id/automation/activity?days=7
 *
 * Lives on /settings/automation alongside the per-connection TierScheduleConfig
 * so the "what's scheduled" and "what's actually happening" views are next
 * to each other.
 */

const JOB_TYPE_LABELS = {
  tier1_discovery: 'Discovery',
  tier2_schema: 'Schema',
  tier3_changes: 'Change detection',
  tier4_profiling: 'Profiling',
  tier5_lineage: 'Lineage',
  tier6_usage: 'Usage analysis',
  validation_run: 'Validation runs',
  anomaly_detection: 'Anomaly detection',
};

function formatRelativeTime(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  const now = new Date();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

const ConnectionActivityPanel = ({ connectionId }) => {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivity = useCallback(async () => {
    if (!connectionId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(
        `/connections/${connectionId}/automation/activity?days=7`
      );
      if (response?.success) {
        setActivity(response);
      } else {
        setError('Could not load activity');
      }
    } catch (err) {
      setError(err?.message || 'Could not load activity');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return (
    <div className="bg-white border border-secondary-200 rounded-lg">
      <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-secondary-900">
            What's been running (last 7 days)
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            Every automated job that has touched this connection. Use this to
            verify that paused connections have actually stopped firing jobs.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchActivity}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border border-secondary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          <ArrowPathIcon
            className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      <div className="p-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading && !activity && (
          <div className="text-sm text-secondary-400">Loading activity…</div>
        )}

        {activity && activity.summary.length === 0 && (
          <div className="rounded-md bg-secondary-50 p-4 text-sm text-secondary-600">
            No automated jobs have run on this connection in the last 7 days.
          </div>
        )}

        {activity && activity.summary.length > 0 && (
          <>
            <div className="text-sm text-secondary-600 mb-3">
              <strong>{activity.total_runs}</strong> total runs across{' '}
              <strong>{activity.summary.length}</strong> job types in the last{' '}
              {activity.days} days.
            </div>
            <ul className="divide-y divide-secondary-200 border-t border-b border-secondary-200">
              {activity.summary.map((item) => (
                <li
                  key={item.job_type}
                  className="px-2 py-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-secondary-900">
                      {JOB_TYPE_LABELS[item.job_type] || item.job_type}
                    </p>
                    <p className="text-xs text-secondary-500">{item.job_type}</p>
                  </div>
                  <div className="flex items-center space-x-6 text-right">
                    <div>
                      <p className="text-sm font-semibold text-secondary-900">
                        {item.count.toLocaleString()}
                      </p>
                      <p className="text-xs text-secondary-500">runs</p>
                    </div>
                    <div className="min-w-[5rem]">
                      <p className="text-sm text-secondary-700 inline-flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1 text-secondary-400" />
                        {formatRelativeTime(item.last_run)}
                      </p>
                      <p className="text-xs text-secondary-500">last run</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default ConnectionActivityPanel;
